/**
 * SYR-107 — deliberate per-domain overrides for the public Hypothesis tool.
 *
 * Only exceptions live here. Domains belonging to an active organisation are
 * NOT listed: they are derived live at check time, because a hand-kept list of
 * customers drifts the moment one onboards, and the day it drifts we cold-email
 * a paying customer about a brand they already pay us to measure.
 *
 * The note field is load-bearing. Nobody remembers in six weeks why a domain
 * went on the list, and an unexplained block looks like a bug.
 */

import React, { useState } from 'react';
import {
    Button,
    Card,
    Input,
    Popconfirm,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import { DeleteOutlined, PlusOutlined, SafetyOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    useDeleteHypothesisDomainPolicyMutation,
    useGetHypothesisDomainPoliciesQuery,
    useSaveHypothesisDomainPolicyMutation,
} from '../../../services/nestjs-base-api';
import { IHypothesisDomainPolicy } from '../../../services/nestjs-base-api/types';

const { Text, Paragraph } = Typography;

const POLICY_COLOUR: Record<IHypothesisDomainPolicy['policy'], string> = {
    auto: 'green',
    manual: 'orange',
    blocked: 'red',
};

interface Props {
    jwt: string;
}

export const DomainPolicyCard: React.FC<Props> = ({ jwt }) => {
    const intl = useIntl();
    const [domain, setDomain] = useState('');
    const [policy, setPolicy] = useState<IHypothesisDomainPolicy['policy']>('manual');
    const [note, setNote] = useState('');

    const { data: policies, isFetching } = useGetHypothesisDomainPoliciesQuery(
        { jwtToken: jwt },
        { skip: !jwt },
    );
    const [save, saveState] = useSaveHypothesisDomainPolicyMutation();
    const [remove] = useDeleteHypothesisDomainPolicyMutation();

    const add = async () => {
        if (!domain.trim()) return;
        try {
            await save({ jwtToken: jwt, domain: domain.trim(), policy, note: note.trim() }).unwrap();
            setDomain('');
            setNote('');
            message.success(
                intl.formatMessage({
                    id: 'publicHypothesis.policy.saved',
                    defaultMessage: 'Saved.',
                }),
            );
        } catch {
            message.error(
                intl.formatMessage({
                    id: 'publicHypothesis.policy.saveFailed',
                    defaultMessage: 'That domain could not be saved.',
                }),
            );
        }
    };

    const columns = [
        {
            title: intl.formatMessage({ id: 'publicHypothesis.policy.domain', defaultMessage: 'Domain' }),
            dataIndex: 'domain',
            key: 'domain',
            render: (value: string) => <Text strong>{value}</Text>,
        },
        {
            title: intl.formatMessage({ id: 'publicHypothesis.policy.policy', defaultMessage: 'Policy' }),
            dataIndex: 'policy',
            key: 'policy',
            render: (value: IHypothesisDomainPolicy['policy']) => (
                <Tag color={POLICY_COLOUR[value]}>{value}</Tag>
            ),
        },
        {
            title: intl.formatMessage({ id: 'publicHypothesis.policy.note', defaultMessage: 'Why' }),
            dataIndex: 'note',
            key: 'note',
            render: (value: string | null) =>
                value ? <Text>{value}</Text> : <Text type="secondary">—</Text>,
        },
        {
            title: '',
            key: 'actions',
            width: 60,
            render: (_: unknown, row: IHypothesisDomainPolicy) => (
                <Popconfirm
                    title={intl.formatMessage({
                        id: 'publicHypothesis.policy.deleteConfirm',
                        defaultMessage: 'Remove this override? The domain goes back to the default.',
                    })}
                    onConfirm={() => remove({ jwtToken: jwt, id: row.id })}
                >
                    <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        },
    ];

    return (
        <Card
            title={
                <span>
                    <SafetyOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage
                        id="publicHypothesis.policy.title"
                        defaultMessage="Domain overrides"
                    />
                </span>
            }
            style={{ marginTop: 24 }}
        >
            <Paragraph type="secondary">
                <FormattedMessage
                    id="publicHypothesis.policy.intro"
                    defaultMessage="Exceptions only. Anything not listed is allowed to run if the rest of the gate passes, and existing customers are recognised automatically, so do not list them here. Use manual for Adir's brands and their direct competitors."
                />
            </Paragraph>

            <Space.Compact style={{ width: '100%', marginBottom: 16 }}>
                <Input
                    placeholder={intl.formatMessage({
                        id: 'publicHypothesis.policy.domainPlaceholder',
                        defaultMessage: 'example.com.au',
                    })}
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    style={{ maxWidth: 260 }}
                />
                <Select
                    value={policy}
                    onChange={setPolicy}
                    style={{ width: 130 }}
                    options={[
                        { value: 'manual', label: 'Manual' },
                        { value: 'blocked', label: 'Blocked' },
                        { value: 'auto', label: 'Auto' },
                    ]}
                />
                <Input
                    placeholder={intl.formatMessage({
                        id: 'publicHypothesis.policy.notePlaceholder',
                        defaultMessage: 'Why (you will not remember in six weeks)',
                    })}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                />
                <Button
                    type="primary"
                    icon={<PlusOutlined style={{ marginRight: 4 }} />}
                    loading={saveState.isLoading}
                    onClick={add}
                >
                    <FormattedMessage id="publicHypothesis.policy.add" defaultMessage="Add" />
                </Button>
            </Space.Compact>

            <Table
                dataSource={policies ?? []}
                columns={columns}
                rowKey="id"
                loading={isFetching}
                size="small"
                pagination={false}
            />
        </Card>
    );
};
